import { getStatsigClient } from 'chimera/all/plugins/tracking/statsig/install'

/**
 * Composable for A/B testing with Statsig.
 * @see docs/experiments.md for usage examples
 *
 * @param {object} store - Vuex store instance
 * @returns {object} Experiment utilities
 */
export function useExperiments(store) {
  /**
   * @param {string} id - Statsig experiment ID
   * @returns {Promise<object>}
   */
  function getExperiment(id) {
    return store.dispatch('experiments/get', { id })
  }

  /**
   * @param {object} experiment
   * @returns {boolean}
   */
  function isControl(experiment) {
    return experiment?.variant === 0
  }

  /**
   * @param {object} experiment
   * @param {string|number} variant
   * @returns {boolean}
   */
  function isVariant(experiment, variant) {
    return experiment?.variant === Number(variant)
  }

  /**
   * @returns {Array}
   */
  function getAll() {
    return store.getters['experiments/all']
  }

  /**
   * Synchronous store lookup by ID. Does not trigger a Statsig exposure.
   * @param {string} id
   * @returns {object|undefined}
   */
  function getFromStore(id) {
    return store.getters['experiments/all'].find((exp) => exp.id === id)
  }

  /**
   * @param {string} id
   * @returns {boolean}
   */
  function isAssigned(id) {
    return store.getters['experiments/isAssigned'](id)
  }

  /**
   * @param {string} id
   * @param {number} variant
   */
  function override(id, variant) {
    store.commit('experiments/addOverride', { id, variant: Number(variant) })

    const client = getStatsigClient()
    client?.overrideAdapter?.overrideExperiment(id, {
      variant: Number(variant),
    })
  }

  /**
   * @param {string} id
   */
  function clearOverride(id) {
    store.commit('experiments/removeOverride', id)

    const client = getStatsigClient()
    client?.overrideAdapter?.removeExperimentOverride(id)
  }

  return {
    getExperiment,
    isControl,
    isVariant,
    getAll,
    getFromStore,
    isAssigned,
    override,
    clearOverride,
  }
}

export default useExperiments
